import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ContractService } from 'src/app/contract/shared/contract.service';
import { ToastrService } from 'ngx-toastr';
import { UserService } from 'src/app/shared/user.service';
import * as moment from 'moment-timezone';
import { AddNoteDialogComponent } from './add-note-dialog/add-note-dialog.component';
import { ConfirmDialogComponent } from 'src/app/contract/contract-master/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-contract-notes',
  templateUrl: './contract-notes.component.html',
  styleUrls: ['./contract-notes.component.scss']
})
export class ContractNotesComponent implements OnInit {
  @Input() contractId: number;
  @Input() mode: string;
  @Output() refreshContract = new EventEmitter<void>();

  notes: any[] = [];
  notesLoading: boolean = false;

  constructor(
    private contractService: ContractService,
    private toast: ToastrService,
    private dialog: MatDialog,
    public userService: UserService
  ) { }

  ngOnInit(): void {
    if (this.contractId) {
      this.getNotes();
    }
  }

  async getNotes() {
    if (!this.contractId) {
      return;
    }
    
    this.notesLoading = true;
    try {
      const res: any = await this.contractService.getNotes(this.contractId);
      if (Array.isArray(res)) {
        // Sort notes by createDate (newest first)
        this.notes = res.sort((a, b) => {
          const dateA = new Date(a.createDate || 0);
          const dateB = new Date(b.createDate || 0);
          return dateB.getTime() - dateA.getTime();
        });
      } else {
        this.notes = [];
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      this.toast.error('Failed to load notes. Please try again later.', '');
      this.notes = [];
    } finally {
      this.notesLoading = false;
    }
  }

  formatDateTime(value: string): string {
    if (!value) return 'N/A';
    return moment(value).isValid()
      ? moment(value).format("MMM D, YYYY h:mm A")
      : value;
  }

  refreshNotes(): void {
    this.getNotes();
  }

  openAddNoteDialog(): void {
    const dialogRef = this.dialog.open(AddNoteDialogComponent, {
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '500ms',
      width: '40%',
      disableClose: true,
      data: {
        contractId: this.contractId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh notes list after successful add/edit
        this.getNotes();
        this.refreshContract.emit();
      }
    });
  }

  openEditNoteDialog(note: any): void {
    // Check if user can edit this note (only own notes)
    const currentUserId = this.userService.loggedinUser?.id?.toString();
    if (note.createdBy !== currentUserId) {
      this.toast.warning('You can only edit your own notes', '');
      return;
    }

    const dialogRef = this.dialog.open(AddNoteDialogComponent, {
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '500ms',
      width: '40%',
      disableClose: true,
      data: {
        contractId: this.contractId,
        note: note
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh notes list after successful edit
        this.getNotes();
        this.refreshContract.emit();
      }
    });
  }

  canEditNote(note: any): boolean {
    const currentUserId = this.userService.loggedinUser?.id?.toString();
    return note.createdBy === currentUserId;
  }

  canDeleteNote(note: any): boolean {
    const currentUserId = this.userService.loggedinUser?.id?.toString();
    return note.createdBy === currentUserId;
  }

  openDeleteNoteDialog(note: any): void {
    // Check if user can delete this note (only own notes)
    const currentUserId = this.userService.loggedinUser?.id?.toString();
    if (note.createdBy !== currentUserId) {
      this.toast.warning('You can only delete your own notes', '');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '500ms',
      width: '30%',
      disableClose: true,
      data: {
        title: 'Delete Note',
        message: 'Are you sure you want to delete this note?',
        confirmText: 'Yes, Delete',
        cancelText: 'Cancel',
        confirmButtonColor: 'warn',
        // icon: 'heroTrash',
        confirmFunction: () => this.deleteNote(note, dialogRef)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      // Dialog closed, no action needed here
    });
  }

  async deleteNote(note: any, dialogRef?: MatDialogRef<ConfirmDialogComponent>): Promise<void> {
    if (!this.contractId || !note.id) {
      this.toast.error('Unable to delete note. Missing required information.', '');
      if (dialogRef) {
        dialogRef.close();
      }
      return;
    }

    try {
      await this.contractService.deleteNotes(this.contractId, note.id);
      this.toast.success('Note deleted successfully', '');
      // Close the dialog if it exists
      if (dialogRef) {
        dialogRef.close();
      }
      // Refresh notes list after successful deletion
      this.getNotes();
      this.refreshContract.emit();
    } catch (error) {
      console.error('Error deleting note:', error);
      this.toast.error('Failed to delete note. Please try again later.', '');
      if (dialogRef) {
        dialogRef.close();
      }
    }
  }
 
}

